import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Search,
  Plus,
  Film,
  Tag,
  MapPin,
  Clapperboard,
  Sparkles,
  CornerDownLeft,
  CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTriggerAiRun } from "@/hooks/useAiAgent";
import { useRunScraper } from "@/hooks/useScrapers";
import { cn } from "@/lib/utils";
import { useLayout } from "./LayoutContext";
import { PAGE_NAV } from "./navigation";
import type { LucideIcon } from "lucide-react";

interface CommandItem {
  id: string;
  icon: LucideIcon;
  label: string;
  hint: string;
  run: () => void;
}

interface CommandSection {
  label: string;
  items: CommandItem[];
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { commandOpen, setCommandOpen, setQuickAdd } = useLayout();
  const triggerAiRun = useTriggerAiRun();
  const runOck = useRunScraper();

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const isAdmin = userRole === "admin";
  const role = userRole === "organizer" ? "organizer" : "admin";

  const close = () => setCommandOpen(false);

  const sections: CommandSection[] = useMemo(() => {
    const quickActions: CommandItem[] = isAdmin
      ? [
          {
            id: "add-event",
            icon: Plus,
            label: "Dodaj wydarzenie",
            hint: "Otwiera formularz tworzenia",
            run: () => setQuickAdd("event"),
          },
          {
            id: "add-parent-event",
            icon: CalendarDays,
            label: "Dodaj cykl wydarzeń",
            hint: "Nowe wydarzenie nadrzędne",
            run: () => setQuickAdd("parent-event"),
          },
          {
            id: "add-category",
            icon: Tag,
            label: "Dodaj kategorię",
            hint: "Otwiera formularz tworzenia",
            run: () => setQuickAdd("category"),
          },
          {
            id: "add-venue",
            icon: MapPin,
            label: "Dodaj lokalizację",
            hint: "Otwiera formularz tworzenia",
            run: () => setQuickAdd("venue"),
          },
          {
            id: "add-movie",
            icon: Film,
            label: "Dodaj film",
            hint: "Otwiera formularz tworzenia",
            run: () => setQuickAdd("movie"),
          },
          {
            id: "run-ock",
            icon: Clapperboard,
            label: "Uruchom skrobak OCK",
            hint: "Pobiera repertuar OCK",
            run: () =>
              runOck.mutate(undefined, {
                onSuccess: () => toast.success("Uruchomiono skrobak OCK"),
                onError: (e) =>
                  toast.error("Nie udało się uruchomić skrobaka", {
                    description: e instanceof Error ? e.message : undefined,
                  }),
              }),
          },
          {
            id: "run-ai",
            icon: Sparkles,
            label: "Uruchom Agent AI",
            hint: "Wszystkie źródła",
            run: () =>
              triggerAiRun.mutate(undefined, {
                onSuccess: () => toast.success("Uruchomiono Agent AI"),
                onError: (e) =>
                  toast.error("Nie udało się uruchomić agenta", {
                    description: e instanceof Error ? e.message : undefined,
                  }),
              }),
          },
        ]
      : [];

    const goTo: CommandItem[] = PAGE_NAV.filter((p) => p.roles.includes(role)).map((p) => ({
      id: `nav-${p.to}`,
      icon: p.icon,
      label: p.title,
      hint: "Strona",
      run: () => navigate(p.to),
    }));

    const result: CommandSection[] = [];
    if (quickActions.length) result.push({ label: "Szybkie akcje", items: quickActions });
    result.push({ label: "Przejdź do", items: goTo });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, role]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sections
      .map((s) => ({
        ...s,
        items: s.items.filter(
          (it) => !q || it.label.toLowerCase().includes(q) || it.hint.toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [sections, query]);

  const flat = useMemo(() => filtered.flatMap((s) => s.items), [filtered]);

  // Clamp at render rather than storing a corrected index — keeps the
  // highlight valid as the filtered list shrinks without a sync effect.
  const safeIndex = Math.min(activeIndex, Math.max(0, flat.length - 1));

  // Reset query + selection on each open (and refocus the input).
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
    }
    setCommandOpen(open);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(Math.min(flat.length - 1, safeIndex + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(Math.max(0, safeIndex - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flat[safeIndex];
      if (item) {
        item.run();
        close();
      }
    }
  };

  let runningIndex = -1;

  return (
    <DialogPrimitive.Root open={commandOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onKeyDown={onKeyDown}
          className="fixed left-1/2 top-[14vh] z-[101] w-[580px] max-w-[calc(100vw-2rem)] -translate-x-1/2 overflow-hidden rounded-2xl border bg-pulso-surface shadow-2xl duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2"
        >
          <DialogPrimitive.Title className="sr-only">Paleta poleceń</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Szukaj akcji lub stron
          </DialogPrimitive.Description>

          <div className="flex items-center gap-3 border-b border-black/5 px-4 py-3.5">
            <Search className="h-[18px] w-[18px] text-pulso-muted" />
            <input
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              placeholder="Szukaj akcji lub stron..."
              className="flex-1 bg-transparent text-base outline-none placeholder:text-pulso-muted"
            />
            <kbd className="rounded border border-black/10 bg-black/5 px-1.5 py-0.5 text-[11px] font-semibold text-pulso-muted">
              esc
            </kbd>
          </div>

          <div className="max-h-[480px] overflow-y-auto p-2">
            {filtered.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-pulso-muted">
                Brak wyników dla „{query}"
              </div>
            )}
            {filtered.map((section) => (
              <div key={section.label}>
                <div className="px-3 pb-1.5 pt-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-pulso-muted">
                  {section.label}
                </div>
                {section.items.map((item) => {
                  runningIndex += 1;
                  const idx = runningIndex;
                  const Icon = item.icon;
                  const active = idx === safeIndex;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => {
                        item.run();
                        close();
                      }}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                        active ? "bg-black/5" : "hover:bg-black/[0.03]",
                      )}
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pulso-primary/10 text-pulso-primary">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground">
                          {item.label}
                        </span>
                        <span className="block truncate text-xs text-pulso-muted">{item.hint}</span>
                      </span>
                      {active && <CornerDownLeft className="h-3.5 w-3.5 text-pulso-muted" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
